<template>
    <div class="flex-none flex gap-8 mr-8" v-if="application.stage">
        <popper-modal
            v-if="application"
            ref="moveStage"
            align="end"
            :disabled="!can"
            @mouseenter="loadData"
            @focusin="loadData"
        >
            <template #trigger>
                <div
                    class="font-semibold leading-none text-white rounded-32 h-40 flex items-center px-16"
                    :style="{ backgroundColor: application.stageColor }"
                >
                    <span class="pl-8 mr-16">{{ application.stage.name }}</span>
                    <svg-icon type="keyboard-arrow-down" class="w-11 h-7"></svg-icon>
                </div>
            </template>
            <template #content>

                <div class="shadow-md bg-white rounded flex-none p-24">
                    <div class="w-240 mb-16">
                        <select-dropdown
                            :auto-translate="false"
                            v-model="selectedStage"
                            :options="options"
                            no-auto-sort
                        >
                            {{ $t('applications.move_application_to_stage') }}
                        </select-dropdown>
                    </div>

                    <simple-button
                        @click="moveStage"
                        class="w-full"
                        display="secondary"
                        look="primary"
                        :loading="movingStage"
                    >{{ $t('applications.move') }}</simple-button>
                </div>

            </template>
        </popper-modal>

        <tooltip
            v-if="can"
            :content="application.rejected ? $t('applications.unreject') : $t('applications.reject_subject', {subject: application.first_name})"
            offset="0,10"
            delayed
            position="top"
        >
            <button
                @click="application.rejected ? confirmUnreject() : confirmReject()"
                class="reject-button trans-ease h-40 w-40 flex items-center justify-center"
                :class="[ application.rejected ? 'text-semantic-error hover:text-primary-40' : 'text-primary hover:text-semantic-error' ]"
            >
                <svg-icon type="front-hand" class="w-32 h-32"></svg-icon>
            </button>
        </tooltip>

        <div v-else-if="application.rejected">
            <button class="cursor-default h-40 w-40 flex items-center justify-center text-semantic-error">
                <svg-icon type="front-hand"></svg-icon>
            </button>
        </div>

        <popup-modal-confirm
            v-if="rejectingApplication"
            custom-classes="w-624"
            :confirmText="$t('applications.reject_subject', {subject: application.first_name})"
            :message="$t('applications.confirm_reject_one', {name: application.fullName})"
            show-exclamation
            :title="$t('applications.reject_subject', {name: application.fullName}) + '?'"
            :working="performingRejectRequest"
            @close="cancelReject"
            @confirm="reject"
        >
            <rejected-reason v-model="rejectData" :has-external-partner="hasExternalPartner" class="mt-16"/>
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="hiringApplication"
            :title="$t('applications.hire_subject', {subject: application.first_name}) + '?'"
            :message="$t('applications.confirm_hire_one', {name: application.first_name})"
            :confirmText="$t('applications.hire_subject', {subject: application.first_name})"
            :working="performingHireRequest"
            confirm-button="success"
            @confirm="hire"
            @close="cancelHire"
        >
            <hire-reason v-model="hireData" :has-external-partner="hasExternalPartner" class="mt-16"/>
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="unrejectingApplication"
            :title="$t('applications.not_reject_subject') + '?'"
            :message="$t('applications.confirm_unreject', {subject: application.fullName})"
            :confirmText="$t('applications.unreject')"
            :working="performingUnrejectRequest"
            confirm-button="success"
            @confirm="unreject"
            @close="cancelUnreject"
        >
        </popup-modal-confirm>
    </div>
</template>

<script>
import {mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useNotificationsStore} from '../../state/modules/notifications';
import SelectDropdown from '../forms/SelectDropdown.vue';
import SvgIcon from './SvgIcon.vue';
import SimpleButton from './SimpleButton.vue';
import * as applications from '../../services/ApplicationService';
import * as stages from '../../services/StageService';
import Application from '../../models/Application';
import { normalizeJson } from '../../../shared/helpers';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Tooltip from '../navigation/Tooltip.vue';
import PopupModalConfirm from './PopupModalConfirm.vue';
import RejectedReason from "./RejectedReason";
import PopperModal from '../PopperModal.vue';
import Stage from '../../models/Stage';
import PopupModal from "./PopupModal.vue";
import HireReason from "../HireReason.vue";

export default {
    components: {
        HireReason,
        PopupModal,
        RejectedReason,
        'select-dropdown': SelectDropdown,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'tooltip': Tooltip,
        'popup-modal-confirm': PopupModalConfirm,
        'popper-modal': PopperModal,
    },
    props: {
        application: Object,
        can: Boolean
    },
    data: function () {
        return {
            selectedStage: null,
            movingStage: false,
            rejectingApplication: false,
            hiringApplication: false,
            performingHireRequest: false,
            unrejectingApplication: false,
            performingRejectRequest: false,
            performingUnrejectRequest: false,
            rejectData: {
                reason: null,
                comment: "",
                comment_external: "",
            },
            hireData: {
                reason: '',
                comment: "",
                comment_external: "",
            },
            showFeedbackModal: false,
            loaded: false,
        }
    },
    computed: {
        currentStage () {
            return this.application.stage;
        },
        job () {
            return this.application.stage?.job;
        },
        stages () {
            return useRepo(Stage).where('job_id', this.job?.id).orderBy('order').get();
        },
        nextStage () {
            let nextStages = this.otherStages.filter(stage => stage.order > this.currentStage.order);

            return nextStages.length ? nextStages[0] : this.otherStages[this.otherStages.length -1];
        },
        otherStages () {
            return this.stages
            .filter(stage => stage.id !== this.currentStage.id);
        },
        options () {
            return this.otherStages.map(stage => ({
                value: stage.id, label: stage.name,
            }));
        },
        hasExternalPartner(){
          return !!this.application.interim_candidate_id;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        loadData () {
            if (this.can) {
                if (this.loaded) {
                    return;
                }

                this.loaded = true;

                if (this.job) {
                    stages.index(this.job.id).then(result => {
                        return useRepo(Stage).save(normalizeJson(result.data));
                    }).then(() => {
                        this.selectedStage = this.nextStage.id.toString();
                    });
                }
            }
        },
        updateStage(reason = {}) {
            return applications.update(this.application.id, {
                data: {
                    attributes: {
                        ...reason
                    },
                    relationships: { stage: { data: {
                                type: 'stages',
                                id: this.selectedStage,
                            }}}
                }
            }).then(result => {
                return useRepo(Application).save(normalizeJson(result));
            }).then(inserts => {
                this.movingStage = false;
                this.$refs.moveStage.close();
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('applications.moved_successfully', {
                        applicant: this.application.first_name,
                        stage: this.currentStage.name,
                    })
                })
                this.selectedStage = this.nextStage.id.toString();
            });
        },
        moveStage () {
            this.movingStage = true;
            const stage = this.otherStages.find(stage => stage.id === parseInt(this.selectedStage));

            if (stage.onboarding && this.application.interim_id) {
                this.hiringApplication = true;
            } else {
                this.updateStage();
            }
        },
        confirmReject () {
            this.rejectingApplication = true;
        },
        confirmUnreject () {
            this.unrejectingApplication = true;
        },
        cancelReject () {
            this.rejectingApplication = false;
            this.rejectData = {
                reason: null,
                comment: "",
                comment_external: "",
            };
        },
        cancelHire() {
            this.hiringApplication = false;
            this.movingStage = false;
            this.hireData = {
                reason: null,
                comment: "",
                comment_external: "",
            };
        },
        cancelUnreject () {
            this.unrejectingApplication = false;
        },
        hire() {
            this.performingHireRequest = true;

            this.updateHireStatus().then(() => {
                this.performingHireRequest = false;
                this.hiringApplication = false;
                this.hireData = {
                    reason: null,
                    comment: "",
                    comment_external: "",
                };
            });
        },
        reject () {
            this.performingRejectRequest = true;

            this.updateRejectStatus(true).then((inserts) => {
                this.performingRejectRequest = false;
                this.rejectingApplication = false;
            });
        },
        unreject () {
            this.performingUnrejectRequest = true;

            this.updateRejectStatus(false).then((inserts) => {
                this.performingUnrejectRequest = false;
                this.unrejectingApplication = false;
            });
        },
        updateHireStatus() {
            return this.updateStage({
                hire_reason: this.hireData.reason,
                hire_comment: this.hireData.comment,
                hire_comment_external: this.hireData.comment_external,
            });
        },
        updateRejectStatus (status) {
            return applications.update(this.application.id, {
                data: { attributes: {
                        rejected: status,
                        reject_reason: this.rejectData.reason,
                        reject_comment: this.rejectData.comment,
                        reject_comment_external: this.rejectData.comment_external
                }}
            }).then(result => {
                useRepo(Application).save(normalizeJson(result));
                this.rejectData = {
                    reason: null,
                    comment: "",
                    comment_external: "",
                };
            });
        }
    },
    watch: {
        nextStage (stage) {
            if (stage)
                this.selectedStage = stage.id.toString();
        }
    },
}
</script>

<style scoped>
.reject-button:hover {
    transform: scale(1.1);
}
</style>
